import { useEffect, useRef, type ReactNode } from 'react';
import { InlineAlert } from '../../../shared/components/InlineAlert';
import { STATUS_META } from '../format';
import type { BatchStatus } from '../types';

/** Step/page heading. Focus moves here on mount (callers `key` it per step/route), so keyboard and
 * screen-reader users land at the top of new content after every navigation. */
export function StepHeading({ children, id }: { children: ReactNode; id?: string }) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <h2 ref={ref} tabIndex={-1} {...(id ? { id } : {})}>
      {children}
    </h2>
  );
}

/** Error summary: role="alert" (via InlineAlert) and focus moves to it when it appears or its text changes. */
export function ErrorSummary({ message }: { message: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, [message]);
  return (
    <div ref={ref} tabIndex={-1} className="phi-error-summary">
      <InlineAlert>{message}</InlineAlert>
    </div>
  );
}

/** Polite live region for status changes. Always rendered so announcements are reliable. */
export function LiveRegion({ message }: { message: string }) {
  return (
    <div role="status" aria-live="polite" aria-atomic="true" className="phi-sr-only">
      {message}
    </div>
  );
}

export function StatusBadge({ status }: { status: BatchStatus }) {
  const meta = STATUS_META[status];
  const tone = meta.tone === 'neutral' ? '' : ` phi-badge--${meta.tone}`;
  return (
    <span className={`phi-badge${tone}`}>
      <span aria-hidden="true">{meta.icon}</span>
      {meta.label}
    </span>
  );
}

/** Provenance badge shown on every imported record. */
export function ImportedHistoryBadge() {
  return (
    <span className="phi-badge phi-badge--history">
      <span aria-hidden="true">⌛</span>
      Imported history
    </span>
  );
}

export function Pager({
  page, pageSize, totalCount, onPage, label,
}: { page: number; pageSize: number; totalCount: number; onPage: (page: number) => void; label: string }) {
  const pages = Math.max(1, Math.ceil(totalCount / pageSize));
  return (
    <nav className="phi-pager pagination" aria-label={label}>
      {/* aria-disabled (not disabled): a natively disabled button drops keyboard focus the moment the user reaches the
          first/last page, stranding them. The click is ignored instead. */}
      <button type="button" className="phi-btn-secondary" aria-disabled={page <= 1} onClick={() => page > 1 && onPage(page - 1)}>
        Previous
      </button>
      <span aria-live="polite">
        Page {page} of {pages} ({totalCount} {totalCount === 1 ? 'record' : 'records'})
      </span>
      <button type="button" className="phi-btn-secondary" aria-disabled={page >= pages} onClick={() => page < pages && onPage(page + 1)}>
        Next
      </button>
    </nav>
  );
}
